/* eslint-disable @nx/enforce-module-boundaries */
'use client';

import { FormattedInput } from '@payments-checkout/shared/components';
import { isBrazil } from '@payments-checkout/shared/helpers/translations/internacional-address.helpers';
import { analiticaInstance } from '@payments-checkout/shared/plugins/analitica/analitica.events';
import {
  Alert,
  Checkbox,
  CheckboxProps,
  InputField,
  MaskedInput
} from '@stone-payments/jade';
import { useFormTypedValues } from 'modules/checkout/src/app/providers/formValuesProvider.provider';
import { ReactNode, RefAttributes, useEffect, useRef } from 'react';
import {
  Controller,
  useFormContext,
  useWatch
} from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useAddress } from '../../../hooks/useAddress';
import { useCountriesList } from '../../../hooks/useAddress/useCountriesList.hook';
import { useStatesList } from '../../../hooks/useAddress/useStatesList.hook';
import { GridContainer, GridItem } from './address.styles';
import { CountryInput } from './countryInput.component';
import { StatesInput } from './statesInput.component';

type AddressFormComponentProps = {
  formName: 'billing-address' | 'shipping-address';
  screen?: string;
  header?: ReactNode;
};

export function AddressFormComponent({
  formName,
  screen,
  header,
}: AddressFormComponentProps) {
  const { formatMessage, locale } = useIntl();

  const {
    register,
    setValue,
    control,
    getValues,
    formState: { errors },
  } = useFormContext();

  const addressPrefix = formName.includes('-address')
    ? formName
    : `${formName}-address`;

  const field = (fieldName: string) =>
    `${addressPrefix}-${fieldName}`;

  const trackFieldFilled =
    (reference: string) => (event: { target: { value: string } }) => {
      if (!screen || !event.target.value.trim()) return;

      analiticaInstance.events.addressEvents.addressFieldFilled(
        screen,
        reference
      );
    };

  const trackZipCodeFilled = (value: string) => {
    const zipCode = value.trim();

    if (!screen || zipCode.length !== 9) return;
    if (trackedZipCodeRef.current === zipCode) return;

    trackedZipCodeRef.current = zipCode;
    analiticaInstance.events.addressEvents.addressFieldFilled(
      screen,
      'campo_cep'
    );
  };

  const trackStateSelected = (event: { target: { value: string } }) => {
    if (!screen) return;

    analiticaInstance.events.addressEvents.addressStateSelected(
      screen,
      event.target.value
    );
  };

  const trackWithoutNumberSelected = (isSelected: boolean) => {
    if (!screen) return;

    analiticaInstance.events.addressEvents.withoutNumberSelected(
      screen,
      isSelected
    );
  };

  const { formValues } = useFormTypedValues();

  const country = useWatch({
    control,
    name: field('country'),
  }) as string | undefined;

  const zipCode = useWatch({
    control,
    name: field('zip-code'),
  }) as string | undefined;

  const isPassportAndBrazil =
    formValues?.documentType === 'passport' && country === 'BR';

  const withoutNumberAddress = useWatch({
    control,
    name: field('without-number'),
    defaultValue: false,
  }) as boolean;

  const getFieldError = (fieldName: string) =>
    (errors as any)[field(fieldName)];

  const skipNextCepFetch = useRef(
    Boolean(getValues(field('street')) || getValues(field('city')))
  );

  const trackedZipCodeRef = useRef<string | null>(null);

  const {
    isRefetching,
    isLoading,
    refetch: refetchAddress,
  } = useAddress(zipCode as string);

  const {
    data: stateListData,
    isLoading: isLoadingStateList,
    isRefetching: isRefetchingStateList,
    refetch: refetchStateList,
    isError: isErrorStateList,
  } = useStatesList({ language: locale, country: country as string });

  const {
    data: countries,
    isLoading: isCountriesOnLoad,
    isFetching: isCountriesFetching,
    isError: isCountriesError,
  } = useCountriesList(locale);

  const isCountriesLoading = isCountriesOnLoad || isCountriesFetching;
  const isCepLoading = isLoading || isRefetching;
  const hasCountry = Boolean(country);
  const isStateLoading =
    isCountriesLoading || isLoadingStateList || isRefetchingStateList;
  const isStateError = (hasCountry && isErrorStateList) || isCountriesError;

  const handleNewSelectedCountry = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const newCountry = e.target.value;

    if (screen) {
      analiticaInstance.events.addressEvents.addressCountrySelected(
        screen,
        newCountry
      );
    }

    setValue(field('country'), newCountry as any);
    setValue(field('city'), '' as any);
    setValue(field('complement'), '' as any);
    setValue(field('district'), '' as any);
    setValue(field('number'), '' as any);
    setValue(field('state'), '' as any);
    setValue(field('street'), '' as any);
    setValue(field('zip-code'), ' ' as any);
    setValue(field('without-number'), false as any);
  };

  const handleWhenAddressIsWithoutNumber = () => {
    setValue(field('number'), '' as any);
  };

  useEffect(() => {
    if (skipNextCepFetch.current) {
      skipNextCepFetch.current = false;
      return;
    }

    (async () => {
      if ((zipCode as string)?.length === 9 && isBrazil(country as string)) {
        const { data, isFetched, error } = await refetchAddress();
        const addressData = data?.data;

        if (addressData && isFetched) {
          setValue(field('city'), addressData.city as any, {
            shouldValidate: true,
          });
          setValue(field('state'), addressData.state.toUpperCase() as any, {
            shouldValidate: true,
          });
          setValue(field('street'), addressData.street as any, {
            shouldValidate: true,
          });
          setValue(field('district'), addressData.district as any, {
            shouldValidate: true,
          });
        }

        if (error) {
          setValue(field('city'), '' as any);
          setValue(field('state'), '' as any);
          setValue(field('street'), '' as any);
          setValue(field('district'), '' as any);
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zipCode]);

  useEffect(() => {
    if (!country) return;
    refetchStateList();
  }, [country, refetchStateList]);

  useEffect(() => {
    if (!stateListData?.length) return;

    const currentState = getValues(field('state')) as string | undefined;
    if (!currentState) return;

    const matchingOption = stateListData.find(
      (state) => state.code.toUpperCase() === currentState.toUpperCase()
    );
    if (matchingOption) {
      setValue(field('state'), matchingOption.code as any);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateListData]);

  return (
    <div
      data-testid={`${addressPrefix}-container`}
      className="flex flex-col gap-jade-150 bg-jade-background-surface rounded-xl p-jade-250 min-500:p-0 min-500:rounded-none"
    >
      {header && <div className="-mb-jade-150">{header}</div>}

      {isPassportAndBrazil && (
        <div className="my-jade-150">
          <Alert
            variant="warning"
            showIcon
            description={formatMessage({ id: 'addressField.alert' })}
          />
        </div>
      )}

      <div className="flex flex-col gap-jade-300">
        <GridContainer>
          <GridItem>
            <CountryInput
              isCountriesLoading={isCountriesLoading}
              isError={isCountriesError}
              countries={countries}
              onChange={handleNewSelectedCountry}
              data-testid={`${addressPrefix}-country`}
              name={field('country')}
            />
          </GridItem>
          <GridItem>
            {isBrazil(country as string) ? (
              <Controller
                name={field('zip-code')}
                control={control}
                defaultValue={'' as any}
                render={({ field: controllerField }) => (
                  <MaskedInput
                    {...controllerField}
                    label={formatMessage({ id: 'addressField.postalCode' })}
                    placeholder="00000-000"
                    type="text"
                    inputMode="numeric"
                    mask="postalCode"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      controllerField.onChange(e.target.value);
                      trackZipCodeFilled(e.target.value);
                    }}
                    supportText={
                      getFieldError('zip-code')?.message &&
                      formatMessage({
                        id: `addressFields.validate.${getFieldError('zip-code')?.message}`,
                      })
                    }
                    size="medium"
                    error={!!getFieldError('zip-code')?.message}
                    disabled={isCepLoading || isPassportAndBrazil}
                  />
                )}
              />
            ) : (
              <FormattedInput
                data-testid={`${addressPrefix}-cep`}
                formatter="zipCode"
                label={formatMessage({ id: 'addressField.postalCode' })}
                disabled={isCountriesLoading || isPassportAndBrazil}
                supportText={
                  getFieldError('zip-code')?.message &&
                  formatMessage({
                    id: `addressFields.validate.${getFieldError('zip-code')?.message}`,
                  })
                }
                size="medium"
                error={!!getFieldError('zip-code')?.message}
                {...register(field('zip-code'), {
                  required: true,
                  onBlur: trackFieldFilled('campo_cep'),
                })}
              />
            )}
          </GridItem>
          <GridItem>
            <StatesInput
              isStatesLoading={isStateLoading}
              isError={isStateError}
              states={stateListData}
              disabled={isPassportAndBrazil}
              name={field('state')}
              onChange={trackStateSelected}
            />
          </GridItem>
          <GridItem>
            <InputField
              label={formatMessage({ id: 'addressField.city' })}
              disabled={isCepLoading || isPassportAndBrazil}
              size="medium"
              error={!!getFieldError('city')?.message}
              placeholder="Curitiba"
              {...register(field('city'), {
                required: true,
                onBlur: trackFieldFilled('campo_cidade'),
              })}
            />
          </GridItem>
          <GridItem>
            <InputField
              label={formatMessage({ id: 'addressField.street' })}
              disabled={isCepLoading || isPassportAndBrazil}
              placeholder="Avenida Água Verde"
              error={!!getFieldError('street')?.message}
              supportText={
                getFieldError('street')?.message &&
                formatMessage({
                  id: `addressFields.validate.${getFieldError('street')?.message}`,
                })
              }
              size="medium"
              {...register(field('street'), {
                required: true,
                onBlur: trackFieldFilled('campo_endereco'),
              })}
            />
          </GridItem>
          <GridItem>
            <InputField
              label={formatMessage({ id: 'addressField.district' })}
              disabled={isCepLoading || isPassportAndBrazil}
              placeholder="Jardim das Américas"
              error={!!getFieldError('district')?.message}
              size="medium"
              supportText={
                getFieldError('district')?.message &&
                formatMessage({
                  id: `addressFields.validate.${getFieldError('district')?.message}`,
                })
              }
              {...register(field('district'), {
                required: true,
                onBlur: trackFieldFilled('campo_bairro'),
              })}
            />
          </GridItem>
          <GridItem>
            <FormattedInput
              label={`${formatMessage({ id: 'addressField.number' })} ${
                !withoutNumberAddress ? '*' : ''
              }`}
              data-testid={`${addressPrefix}-number`}
              formatter="addressNumber"
              optional={withoutNumberAddress === true}
              placeholder="123, 12A, 13B"
              error={
                withoutNumberAddress
                  ? undefined
                  : !!getFieldError('number')?.message
              }
              supportText={
                getFieldError('number')?.message && !withoutNumberAddress
                  ? formatMessage({
                      id: `addressFields.validate.${getFieldError('number')?.message}`,
                    })
                  : ''
              }
              disabled={withoutNumberAddress || isPassportAndBrazil}
              size="medium"
              {...register(field('number'), {
                required: true,
                onBlur: trackFieldFilled('campo_numero'),
              })}
            />
          </GridItem>
          <GridItem>
            <InputField
              label={formatMessage({ id: 'addressField.complement' })}
              optional
              placeholder="Bl 5 Ap 102"
              error={!!getFieldError('complement')?.message}
              disabled={isPassportAndBrazil}
              size="medium"
              supportText={
                getFieldError('complement')?.message &&
                formatMessage({
                  id: `addressFields.validate.${getFieldError('complement')?.message}`,
                })
              }
              {...register(field('complement'), {
                required: true,
                onBlur: trackFieldFilled('campo_complemento'),
              })}
            />
          </GridItem>
          <Controller
            name={field('without-number')}
            control={control}
            render={({ field: controllerField }) => (
              <Checkbox
                id={`${formName} without number`}
                label={formatMessage({ id: 'addressField.withoutNumber' })}
                {...(controllerField as unknown as CheckboxProps &
                  RefAttributes<HTMLInputElement>)}
                onChange={(e) => {
                  controllerField.onChange(e.target.checked);
                  trackWithoutNumberSelected(e.target.checked);
                  handleWhenAddressIsWithoutNumber();
                }}
                disabled={isPassportAndBrazil}
              />
            )}
          />
        </GridContainer>
      </div>
    </div>
  );
}
